<template>
  <div>
    <div :id="editorId" ref="editor"></div>
    <input
      type="file"
      ref="fileInput"
      @change="onFileChange"
      accept="image/*"
      style="display: none"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import axios from '@/services/axios';
import { sanitizeHtml } from '@/utils/safeHtml';

export default {
  name: 'RichTextEditor',
  props: {
    modelValue:   { type: String, default: '' },
    placeholder:  { type: String, default: 'Edite o conteúdo aqui...' },
    id:           { type: String, default: 'rich-text-editor' },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const editor    = ref(null);
    const quill     = ref(null);
    const fileInput = ref(null);
    const editorId  = computed(() => props.id);

    // Novo tamanho máximo fixo
    const MAX_WIDTH  = 512;
    const MAX_HEIGHT = 384;
    const QUALITY    = 1.0;

    const selectLocalImage = () => {
      fileInput.value.click();
    };

    // Redimensiona SEMPRE para até 512x384
    function resizeImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          let w = img.width;
          let h = img.height;
          // mantém proporção
          const ratio = Math.min(MAX_WIDTH / w, MAX_HEIGHT / h, 1);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);

          const canvas = document.createElement('canvas');
          canvas.width  = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);

          canvas.toBlob(
            blob => {
              if (!blob) return reject(new Error('Falha ao comprimir imagem'));
              resolve(new File([blob], file.name, { type: file.type }));
            },
            file.type,
            QUALITY
          );
        };
        img.onerror = () => reject(new Error('Falha ao ler a imagem'));
        img.src = URL.createObjectURL(file);
      });
    }

    const onFileChange = async e => {
      let file = e.target.files[0];
      if (!file) return;

      try {
        // Sempre redimensiona
        file = await resizeImage(file);
        console.log('Imagem redimensionada para', file);

        const form = new FormData();
        form.append('imagem', file);

        const res = await axios.post('/configuracoes/uploads/imagem', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const url = res.data.url;

        // Insere imagem no final do conteúdo
        const currentHTML = quill.value.root.innerHTML;
        const imgTag      = `<img src="${url}" alt="Imagem" />`;
        quill.value.root.innerHTML = sanitizeHtml(currentHTML + imgTag);

        // Sincroniza v-model
        emit('update:modelValue', sanitizeHtml(quill.value.root.innerHTML));

      } catch (err) {
        console.error('Erro ao processar/upload de imagem:', err);
      } finally {
        e.target.value = '';
      }
    };

    onMounted(() => {
      quill.value = new Quill(editor.value, {
        theme: 'snow',
        placeholder: props.placeholder,
        modules: {
          toolbar: {
            container: [
              [{ header: [1, 2, 3, false] }],
              [{ font: [] }],
              ['bold','italic','underline'],
              [{ color: [] },{ background: [] }],
              [{ script: 'sub'},{ script: 'super'}],
              [{ list: 'ordered'},{ list: 'bullet'}],
              [{ indent: '-1'},{ indent: '+1'}],
              [{ align: [] }],
              ['link','image'],
              ['clean']
            ],
            handlers: { image: selectLocalImage }
          }
        }
      });

      if (props.modelValue) {
        quill.value.root.innerHTML = sanitizeHtml(props.modelValue);
      }

      quill.value.on('text-change', () => {
        emit('update:modelValue', sanitizeHtml(quill.value.root.innerHTML));
      });
    });

    watch(
      () => props.modelValue,
      newVal => {
        if (quill.value && newVal !== quill.value.root.innerHTML) {
          quill.value.root.innerHTML = sanitizeHtml(newVal);
        }
      }
    );

    return { editor, editorId, fileInput, onFileChange };
  }
};
</script>

<style scoped>
#rich-text-editor { min-height: 200px; }
.ql-editor img {
  max-width: 100%;
  display: block;
  margin-top: 1rem;
}
</style>
